// ─── Game modes ─────────────────────────────────────────────────────
// The difficulty and question-count pickers are gone. A player chooses a
// mood, and the recipe behind it is ours: fixed counts keep scores
// comparable inside a mode, and the engine's metrics (pace, steadiness,
// resilience) calibrate once instead of stretching across 5 and 20 question
// games. Design: docs/wot_game_modes_design.md.
//
// This file is the single source of truth for the CLIENT and for scripts.
// The server has its own copy of these recipes inside the
// get_mode_questions RPC, because edge functions and SQL cannot import
// TypeScript. Changing a recipe means changing BOTH; the RPC's comment
// points back here.

export const MODE_IDS = ['RUSH', 'CLIMB', 'SURVIVAL'] as const;
export type ModeId = (typeof MODE_IDS)[number];

/** How many questions of each band a fixed-length mode serves. */
export interface BandMix {
  easy: number;
  medium: number;
  hard: number;
}

export interface ModeSpec {
  id: ModeId;
  /** Card title. UI language is English; no separate TR name by design. */
  name: string;
  /** The one-line promise on the mode card. */
  tagline: string;
  /**
   * Fixed question count, or null for endless (SURVIVAL). Endless modes
   * fetch in chunks of SURVIVAL_CHUNK and climb SURVIVAL_LADDER instead of
   * following a mix.
   */
  questionCount: number | null;
  /** Band recipe for fixed-count modes; null when the ladder decides. */
  mix: BandMix | null;
  /**
   * Seconds on the clock per band. RUSH deliberately flattens this to one
   * number: with no hard questions in the mode, a single fast clock is the
   * mechanic itself.
   */
  timerSeconds: { easy: number; medium: number; hard: number };
  /**
   * Multiplier on the speed component of scoring. RUSH doubles it so that
   * speed, not mere correctness, is what separates two finishers.
   */
  speedBonus: number;
  /** Wrong answers a player survives. Null = mistakes cost points, not life. */
  lives: number | null;
  /** Serve easiest first. True for every mode today; kept explicit. */
  progressive: boolean;
}

export const MODES: Record<ModeId, ModeSpec> = {
  // The coffee-break game. Six easy and one medium, no hard at all: a new
  // player's first contact must not be the specialist tail of the pool.
  // 2026-10: was 5/2; real players answer medium near 37%, and players
  // under 40% on day one rarely came back (20261004_easier_match_mix). This is the
  // average player's mix: solo play adapts it (20261005_adaptive_serving),
  // a player's first game and weak players get 7/0, strong players 5/2.
  RUSH: {
    id: 'RUSH',
    name: 'Rush',
    tagline: '7 questions, one fast clock. Speed pays.',
    questionCount: 7,
    mix: { easy: 6, medium: 1, hard: 0 },
    timerSeconds: { easy: 16, medium: 16, hard: 16 },
    speedBonus: 2,
    lives: null,
    progressive: true,
  },

  // The canonical game and the ranked spine: eight easy, one medium and one
  // genuinely hard question placed at the summit. 2026-10: was 6/3/1, which
  // played at about 56% for real players (20261004_easier_match_mix). A
  // player's very first solo game is all easy (20261005_adaptive_serving);
  // after that the mix stays fixed so ranked scores compare.
  CLIMB: {
    id: 'CLIMB',
    name: 'Climb',
    tagline: '10 questions, easy to hard. One summit.',
    questionCount: 10,
    mix: { easy: 8, medium: 1, hard: 1 },
    timerSeconds: { easy: 16, medium: 20, hard: 24 },
    speedBonus: 1,
    lives: null,
    progressive: true,
  },

  // The legitimate home of the brutal hard band: whoever meets it climbed
  // to it on purpose. Primary score is depth reached, not points, because
  // "question 34" is a brag that travels.
  SURVIVAL: {
    id: 'SURVIVAL',
    name: 'Survival',
    tagline: '3 lives. How deep can you go?',
    questionCount: null,
    mix: null,
    timerSeconds: { easy: 16, medium: 20, hard: 24 },
    speedBonus: 1,
    lives: 3,
    progressive: true,
  },
} as const;

// ── Survival tuning ─────────────────────────────────────────────────
// Kept out of the spec object because they are one mode's internals, and
// the flat names keep the store's game loop readable.
//
// The ladder is two-dimensional: the BAND climbs on the numbers below, and
// within a band the REALMS climb too, using the measured challenge marks
// from realms.ts. Questions 1-5 are easy questions from the gentler realms,
// 6-10 easy from the tougher ones, 11-15 medium, 16-20 hard, and past 20
// the deck serves only the hardest realms' hard pool: the genuine edge.
// The realm ramp is built into the deck server-side (start-solo); this
// module only needs the band for the clock.

/** Question number at which each band begins. Questions 1-10 are easy. */
export const SURVIVAL_LADDER = { mediumFrom: 11, hardFrom: 16 } as const;

/** The clock tightens as the run deepens; the floor keeps it winnable. */
export const SURVIVAL_TIMER = {
  decayEveryQuestions: 5,
  decaySeconds: 1,
  floorSeconds: 8,
} as const;

/** Endless play fetches this many questions per request. */
export const SURVIVAL_CHUNK = 12;

/** Which band a survival run is in at a given 1-based question number. */
export function survivalBand(questionNumber: number): 'easy' | 'medium' | 'hard' {
  if (questionNumber >= SURVIVAL_LADDER.hardFrom) return 'hard';
  if (questionNumber >= SURVIVAL_LADDER.mediumFrom) return 'medium';
  return 'easy';
}

/** Seconds on the clock at a given survival depth, band time minus decay. */
export function survivalTimerSeconds(questionNumber: number): number {
  const band = survivalBand(questionNumber);
  const base = MODES.SURVIVAL.timerSeconds[band];
  const decay = Math.floor((questionNumber - 1) / SURVIVAL_TIMER.decayEveryQuestions)
    * SURVIVAL_TIMER.decaySeconds;
  return Math.max(SURVIVAL_TIMER.floorSeconds, base - decay);
}

/**
 * The longest clock any round can run (CLIMB/SURVIVAL easy). Everything that
 * clamps or normalizes a response time must use this, not a literal: the
 * client's reaction-time clamp, the answer log, and the server's speed-bonus
 * denominator in submit-answer (which mirrors it by hand, Deno cannot import
 * this package).
 */
// 2026-09-04: every band +15% (14/17/21 -> 16/20/24). The first minutes
// decide whether a new player stays; a clock that feels tight on an easy
// question is the fastest way to lose them.
export const MAX_CLOCK_MS = 24_000;

/**
 * Seconds on the clock for one round, whatever the mode. The single source:
 * the store's start paths, nextRound, and the Game screen's timer ring all
 * call this instead of keeping their own copies of the band table, which is
 * how three hardcoded variants of the old 15/12/10 drifted apart.
 * DAILY and multiplayer run on the CLIMB table.
 */
export function roundClockSeconds(
  mode: MatchModeId | null,
  difficulty: 'easy' | 'medium' | 'hard' | undefined,
  questionNumber: number,
): number {
  if (mode === 'RUSH') return MODES.RUSH.timerSeconds.easy;
  if (mode === 'SURVIVAL') return survivalTimerSeconds(questionNumber);
  return MODES.CLIMB.timerSeconds[difficulty ?? 'easy'];
}

export function getMode(id: ModeId): ModeSpec {
  return MODES[id];
}

// ── STREAK BONUS ────────────────────────────────────────────────────
// From the third right answer in a row each right answer pays 10% more, up
// to 50% from the seventh, in every mode, rooms included. The server does
// the scoring (supabase/functions/submit-answer/streak.ts, a hand copy:
// Deno cannot import this package); the app uses this to say what it did.
// streakBonus.test.ts in the client holds the two copies together.
export const STREAK_BONUS_FROM = 3;
export const STREAK_BONUS_STEP_PCT = 10;
export const STREAK_BONUS_MAX_PCT = 50;

/** The bonus, in per cent, on a right answer that makes a run of `streak`. */
export function streakBonusPct(streak: number): number {
  if (!Number.isFinite(streak) || streak < STREAK_BONUS_FROM) return 0;
  return Math.min(STREAK_BONUS_MAX_PCT, (Math.floor(streak) - STREAK_BONUS_FROM + 1) * STREAK_BONUS_STEP_PCT);
}

// ── The Daily ───────────────────────────────────────────────────────
// Not a mode on the picker: a separate surface. Everyone on the planet
// plays the SAME ten questions each UTC day (CLIMB-shaped, 8/1/1, easiest
// first), once. The set is chosen deterministically server-side
// (get_daily_set); the client only needs the day's number for display and
// the day's key for its played-today memory.

/** What a running match can be: the three picker modes, or the daily. */
export type MatchModeId = ModeId | 'DAILY';

/** Day one of the Daily. UTC, so the set flips at the same instant everywhere. */
export const DAILY_EPOCH_UTC = Date.UTC(2026, 7, 20); // 2026-08-20 = DAILY #1

export function dailyNumber(date: Date = new Date()): number {
  return Math.floor((date.getTime() - DAILY_EPOCH_UTC) / 86_400_000) + 1;
}

/** The UTC day key, matching the server's daily_sets.day_key. */
export function dailyKey(date: Date = new Date()): string {
  return date.toISOString().slice(0, 10);
}
